/**
 * PortcoDetail page — full financial detail for a single portfolio company.
 * In live mode: requires authentication via MsalAuthenticationTemplate.
 * In demo mode: loads sample data without authentication.
 */

import { useEffect, useState, useCallback, type ReactNode } from "react";
import { useParams, Link } from "react-router-dom";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import {
  InteractionStatus,
  InteractionType,
  AccountInfo,
} from "@azure/msal-browser";
import { powerbiRequest } from "../authConfig";
import { Loading } from "../ui-components/Loading";
import { LoadingNotice } from "../ui-components/LoadingNotice";
import { ErrorComponent } from "../ui-components/ErrorComponent";
import { MetricCard } from "../ui-components/MetricCard";
import { FinancialTable } from "../ui-components/FinancialTable";
import { DailyTrendChart } from "../ui-components/DailyTrendChart";
import { WeeklyTrendChart } from "../ui-components/WeeklyTrendChart";
import { MonthlyTrendChart } from "../ui-components/MonthlyTrendChart";
import { SalesByCountry } from "../ui-components/SalesByCountry";
import { ForecastChart } from "../ui-components/ForecastChart";
import { ForecastAccuracyChart } from "../ui-components/ForecastAccuracyChart";
import { InventoryChart } from "../ui-components/InventoryChart";
import { SalesByItemSection } from "../ui-components/SalesByItemSection";
import { DebugPanel } from "../ui-components/DebugPanel";
import { OpenOrdersTable } from "../ui-components/OpenOrdersTable";
import {
  getPortcoDebug,
  isDemoMode,
  PortcoMetrics,
  WeeklyTrendPoint,
  MonthlyTrendPoint,
  ForecastAccuracyData,
  InventoryTrendPoint,
} from "../utils/PowerBIApiCall";
import { usePortcoData } from "../hooks/usePortcoData";
import { useIsAdmin } from "../hooks/useIsAdmin";
import {
  DashboardSettings,
  useDashboardFeatures,
} from "../ui-components/DashboardSettings";
import { PortcoSubNav } from "../ui-components/PortcoSubNav";
import { RefreshButton } from "../ui-components/RefreshButton";
import { useRefreshData } from "../hooks/useRefreshData";
import {
  buildForecast,
  buildPeriodForecast,
  attainmentRate,
} from "../utils/forecastModel";
import { EmailPreviewModal } from "../ui-components/EmailPreviewModal";
import { SendEmailModal } from "../ui-components/SendEmailModal";
import { OpenOrderDetailModal } from "../ui-components/OpenOrderDetailModal";
import posthog from "posthog-js";
import { formatCurrency, formatPctSigned as formatPct } from "../utils/format";
import { SectionLabel } from "../ui-components/SectionLabel";
import { SERIES } from "../utils/chartTheme";

// =============================================================================
// Shared UI — pure presentational component, no MSAL dependency
// =============================================================================

interface PortcoDetailUIProps {
  metrics: PortcoMetrics | null;
  loading: boolean;
  error: string | null;
  portcoId?: string;
  weeklyTrend?: WeeklyTrendPoint[] | null;
  monthlyTrend?: MonthlyTrendPoint[] | null;
  forecastAccuracy?: ForecastAccuracyData | null;
  inventoryTrend?: InventoryTrendPoint[] | null;
  debugData?: unknown;
  debugLoading?: boolean;
  debugError?: string | null;
  onDebug?: () => void;
  onSendEmail?: () => void;
  emailSending?: boolean;
  emailStatus?: { success: boolean; message: string } | null;
  /** Selected "as of" date for the page (YYYY-MM-DD). Empty string = latest. */
  reportDate?: string;
  onReportDateChange?: (next: string) => void;
  /** Optional render slot for the Send Email recipient prompt modal. */
  sendEmailModal?: ReactNode;
}

function PortcoDetailUI({
  metrics,
  loading,
  error,
  portcoId,
  weeklyTrend,
  monthlyTrend,
  forecastAccuracy,
  inventoryTrend,
  debugData,
  debugLoading,
  debugError,
  onDebug,
  onSendEmail,
  emailSending,
  emailStatus,
  reportDate,
  onReportDateChange,
  sendEmailModal,
}: PortcoDetailUIProps) {
  const [debugOpen, setDebugOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const { features, toggle, resetAll } = useDashboardFeatures();
  const { refresh, refreshing } = useRefreshData();

  // Today's date in YYYY-MM-DD (local). Used as max= on the date input.
  const todayIso = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  })();

  // Track portco views in PostHog
  useEffect(() => {
    if (metrics && portcoId) {
      posthog.capture("portco_viewed", {
        portcoId,
        portcoName: metrics.portcoName,
      });
    }
  }, [portcoId, metrics]);

  if (loading) {
    return (
      <div className="w-full max-w-6xl space-y-6">
        <LoadingNotice message="Loading financial data — this can take a few seconds…" />
        {/* Skeleton header */}
        <div className="flex items-center justify-between mb-2">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-8 w-20 bg-gray-100 rounded-md animate-pulse" />
            <div className="h-8 w-16 bg-gray-100 rounded-md animate-pulse" />
          </div>
        </div>
        <div>
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
        </div>
        {/* Skeleton metric cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200/70 rounded-xl shadow-card p-5"
            >
              <div className="h-3 w-20 bg-gray-100 rounded animate-pulse mx-auto mb-3" />
              <div className="h-7 w-28 bg-gray-200 rounded animate-pulse mx-auto" />
            </div>
          ))}
        </div>
        {/* Skeleton table */}
        <div className="bg-white border border-gray-200/70 rounded-xl shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
          </div>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="px-6 py-4 border-b border-gray-50 flex items-center gap-6"
            >
              <div className="h-4 w-12 bg-gray-100 rounded-full animate-pulse" />
              <div className="flex-1 grid grid-cols-5 gap-6">
                {[...Array(5)].map((_, j) => (
                  <div
                    key={j}
                    className="h-4 bg-gray-100 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-lg">
        <div className="bg-white border border-red-100 rounded-xl shadow-card overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-1">
                  Something went wrong
                </h3>
                <p className="text-gray-500 text-sm">{error}</p>
              </div>
            </div>
          </div>
        </div>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 mt-4 text-sm text-gray-500 hover:text-wmp-500 transition-colors no-underline"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Dashboard
        </Link>
      </div>
    );
  }

  if (!metrics) return null;

  const m = metrics;

  return (
    <div className="w-full max-w-6xl">
      {/* Top bar: breadcrumb + utility actions */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-wmp-500 transition-colors no-underline group"
        >
          <svg
            className="w-4 h-4 text-gray-400 group-hover:text-wmp-500 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Dashboard
        </Link>
        <div className="flex items-center flex-wrap gap-2">
          {onReportDateChange && (
            <div className="flex items-center gap-1.5">
              <label
                htmlFor="report-date-input"
                className="text-xs font-medium text-gray-500"
              >
                As of
              </label>
              <input
                id="report-date-input"
                type="date"
                value={reportDate ?? ""}
                max={todayIso}
                onChange={(e) => onReportDateChange(e.target.value)}
                className="text-xs font-medium px-3 py-1.5 bg-white text-gray-600 border border-gray-200/70 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-wmp-500/40 focus:border-wmp-500"
                title="View dashboard and email as of a specific date (leave empty for latest)"
              />
              {reportDate && (
                <button
                  onClick={() => onReportDateChange("")}
                  className="text-xs text-gray-400 hover:text-gray-600 px-1"
                  title="Clear (use latest available)"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {portcoId && (
            <button
              onClick={() => setEmailPreviewOpen(true)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200/70 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
            >
              Preview Email
            </button>
          )}
          {onSendEmail && (
            <button
              onClick={onSendEmail}
              disabled={emailSending}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors shadow-sm ${
                emailSending
                  ? "border-gray-200/70 bg-white text-gray-400 cursor-not-allowed"
                  : "border-gray-200/70 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              {emailSending ? "Sending..." : "Send Email"}
            </button>
          )}
          {emailStatus && (
            <span
              className={`text-xs px-2 py-1 rounded ${
                emailStatus.success
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              {emailStatus.message}
            </span>
          )}
          {onDebug && (
            <button
              onClick={() => {
                if (!debugData && !debugLoading) {
                  onDebug();
                }
                setDebugOpen(!debugOpen);
              }}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors shadow-sm ${
                debugOpen
                  ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "border-gray-200/70 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              {debugLoading ? "Loading..." : "Debug"}
            </button>
          )}
          <DashboardSettings
            features={features}
            onToggle={toggle}
            onReset={resetAll}
          />
        </div>
      </div>

      {/* Debug Panel — collapsible raw request/response viewer */}
      {debugOpen && onDebug && (
        <DebugPanel
          debugData={debugData}
          debugLoading={!!debugLoading}
          debugError={debugError ?? null}
          onDebug={onDebug}
          onClose={() => setDebugOpen(false)}
        />
      )}

      {/* Page header: name left, date right */}
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            {m.portcoName}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Daily Financial Performance
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm text-gray-400 pb-0.5">
            {m.reportDateFormatted}
          </span>
          <RefreshButton onClick={refresh} refreshing={refreshing} />
        </div>
      </div>

      {/* Sub-page tab navigation */}
      {portcoId && <PortcoSubNav portcoId={portcoId} />}

      <div className="mt-6" />

      {/* ── TODAY ──────────────────────────────────────────── */}
      <SectionLabel label="Today" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <MetricCard
          label="Daily Revenue"
          value={formatCurrency(m.daily.dailySales)}
          highlighted
        />
        <MetricCard
          label="Daily Budget"
          value={formatCurrency(m.daily.dailyBudget)}
        />
        <MetricCard
          label="Prior Year"
          value={formatCurrency(m.daily.priorYearDailySales)}
        />
        <MetricCard
          label="VS Budget"
          value={formatPct(m.daily.varianceVsBudgetPct)}
          color={
            m.daily.varianceVsBudgetPct >= 0 ? SERIES.positive : SERIES.negative
          }
          formula="(Daily Sales - Daily Budget) ÷ Daily Budget"
          formulaValues={`= (${formatCurrency(m.daily.dailySales)} - ${formatCurrency(m.daily.dailyBudget)}) ÷ ${formatCurrency(m.daily.dailyBudget)} = ${formatPct(m.daily.varianceVsBudgetPct)}`}
        />
      </div>

      {/* ── PERIOD PERFORMANCE ─────────────────────────────── */}
      <SectionLabel label="Period Performance" />
      {(() => {
        // Build regression forecast once, reuse for MTD/QTD/YTD
        const fcst =
          m.dailyTrend && m.dailyTrend.length > 2
            ? buildForecast(m.dailyTrend, m.mtd, m.mtd.budgetFull)
            : null;

        // QTD/YTD: rest of this month from the regression, future months from
        // their own budgets scaled by year-to-date attainment. Open orders are
        // excluded — the daily rate already covers that pipeline.
        const attainment = attainmentRate(m.ytd);
        const qtdForecast = fcst
          ? buildPeriodForecast(
              m.qtd,
              m.mtd,
              fcst.summary.forecastTotal,
              attainment,
            )
          : undefined;
        const ytdForecast = fcst
          ? buildPeriodForecast(
              m.ytd,
              m.mtd,
              fcst.summary.forecastTotal,
              attainment,
            )
          : undefined;

        return (
          <div className="bg-white border border-gray-200/70 rounded-xl shadow-card overflow-hidden mb-8">
            <FinancialTable
              rows={[
                {
                  label: "MTD",
                  metrics: m.mtd,
                  regressionForecast: fcst?.summary.forecastTotal,
                },
                {
                  label: "QTD",
                  metrics: m.qtd,
                  regressionForecast: qtdForecast,
                },
                {
                  label: "YTD",
                  metrics: m.ytd,
                  regressionForecast: ytdForecast,
                },
              ]}
              onOpenOrderClick={() => setOrderDetailOpen(true)}
            />
          </div>
        );
      })()}

      {/* ── TRENDS ─────────────────────────────────────────── */}
      {m.dailyTrend && m.dailyTrend.length > 1 && (
        <>
          <SectionLabel label="Trends" />

          <DailyTrendChart data={m.dailyTrend} />

          {/* Weekly + Monthly side by side */}
          {((weeklyTrend && weeklyTrend.length > 1) ||
            (monthlyTrend && monthlyTrend.length > 1)) && (
            <div
              className={
                weeklyTrend &&
                weeklyTrend.length > 1 &&
                monthlyTrend &&
                monthlyTrend.length > 1
                  ? "grid grid-cols-1 lg:grid-cols-2 gap-4"
                  : ""
              }
            >
              {weeklyTrend && weeklyTrend.length > 1 && (
                <WeeklyTrendChart data={weeklyTrend} />
              )}
              {monthlyTrend && monthlyTrend.length > 1 && (
                <MonthlyTrendChart data={monthlyTrend} />
              )}
            </div>
          )}

          {/* Inventory Chart (Wellmore only, full width) */}
          {inventoryTrend && inventoryTrend.length > 1 && (
            <InventoryChart data={inventoryTrend} />
          )}
        </>
      )}

      {/* ── SALES BY COUNTRY ───────────────────────────────── */}
      {/* Only rendered for portcos whose server config supplies the breakdown
          (today: Primal Harvest). Gated on data presence, not on a slug. */}
      {m.countryTrend && m.countryTrend.length > 0 && (
        <SalesByCountry data={m.countryTrend} summary={m.countrySummary} />
      )}

      {/* Sales by Item (NetSuite) */}
      {portcoId && <SalesByItemSection portcoId={portcoId} />}

      {/* Open Orders Section */}
      <OpenOrdersTable
        totalOpenOrders={m.totalOpenOrders}
        channels={m.openOrderChannels}
        suppressBudget={m.suppressOpenOrderBudget}
      />

      {/* ── FORECAST ───────────────────────────────────────── */}
      {m.dailyTrend && m.dailyTrend.length > 1 && (
        <>
          <SectionLabel label="Forecast" />

          {m.dailyTrend.length > 2 && (
            <ForecastChart
              data={m.dailyTrend}
              mtdMetrics={m.mtd}
              budgetFull={m.mtd.budgetFull}
              openOrders={m.totalOpenOrders}
            />
          )}

          {forecastAccuracy &&
            (forecastAccuracy.accuracyHistory.length > 0 ||
              forecastAccuracy.currentMonthTrajectory.length > 1) && (
              <ForecastAccuracyChart data={forecastAccuracy} />
            )}
        </>
      )}

      {/* Email Preview Modal */}
      {emailPreviewOpen && portcoId && (
        <EmailPreviewModal
          portcoId={portcoId}
          reportDate={reportDate}
          onClose={() => setEmailPreviewOpen(false)}
        />
      )}

      {/* Send Email recipient prompt (rendered by parent loader) */}
      {sendEmailModal}

      {/* Open Order Detail Modal */}
      {orderDetailOpen && portcoId && (
        <OpenOrderDetailModal
          portcoId={portcoId}
          onClose={() => setOrderDetailOpen(false)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Demo mode data loader — no MSAL hooks
// =============================================================================

function DemoPortcoDetailContent() {
  const { portcoId } = useParams<{ portcoId: string }>();
  const [reportDate, setReportDate] = useState<string>("");
  const data = usePortcoData(
    portcoId,
    true,
    undefined,
    reportDate || undefined,
  );

  return (
    <PortcoDetailUI
      metrics={data.metrics}
      loading={data.loading}
      error={data.error}
      portcoId={portcoId}
      weeklyTrend={data.weeklyTrend}
      monthlyTrend={data.monthlyTrend}
      forecastAccuracy={data.forecastAccuracy}
      inventoryTrend={data.inventoryTrend}
      reportDate={reportDate}
      onReportDateChange={setReportDate}
    />
  );
}

// =============================================================================
// Live mode data loader — uses MSAL hooks (must be inside MsalProvider)
// =============================================================================

function LivePortcoDetailContent() {
  const { portcoId } = useParams<{ portcoId: string }>();
  const { instance, inProgress } = useMsal();
  const { isAdmin } = useIsAdmin();
  const [debugData, setDebugData] = useState<unknown>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [reportDate, setReportDate] = useState<string>("");

  const handleAuthError = useCallback(() => {
    instance.acquireTokenRedirect({
      ...powerbiRequest,
      account: instance.getActiveAccount() as AccountInfo,
    });
  }, [instance]);

  const data = usePortcoData(
    portcoId,
    inProgress === InteractionStatus.None,
    handleAuthError,
    reportDate || undefined,
  );

  const handleOpenSendEmail = () => {
    if (!portcoId) return;
    setEmailStatus(null);
    setSendEmailOpen(true);
  };

  const handleSent = (message: string) => {
    if (portcoId) {
      posthog.capture("email_sent", {
        portcoId,
        reportDate: reportDate || null,
      });
    }
    setEmailStatus({ success: true, message });
    setTimeout(() => setEmailStatus(null), 5000);
  };

  const handleDebug = async () => {
    if (!portcoId) return;
    try {
      setDebugLoading(true);
      setDebugError(null);
      const result = await getPortcoDebug(portcoId);
      setDebugData(result);
    } catch (e) {
      setDebugError(e instanceof Error ? e.message : "Debug request failed");
    } finally {
      setDebugLoading(false);
    }
  };

  return (
    <PortcoDetailUI
      metrics={data.metrics}
      loading={data.loading}
      error={data.error}
      portcoId={portcoId}
      weeklyTrend={data.weeklyTrend}
      monthlyTrend={data.monthlyTrend}
      forecastAccuracy={data.forecastAccuracy}
      inventoryTrend={data.inventoryTrend}
      debugData={isAdmin ? debugData : undefined}
      debugLoading={isAdmin ? debugLoading : undefined}
      debugError={isAdmin ? debugError : undefined}
      onDebug={isAdmin ? handleDebug : undefined}
      onSendEmail={isAdmin ? handleOpenSendEmail : undefined}
      emailSending={false}
      emailStatus={isAdmin ? emailStatus : undefined}
      reportDate={reportDate}
      onReportDateChange={setReportDate}
      sendEmailModal={
        isAdmin && sendEmailOpen && portcoId ? (
          <SendEmailModal
            portcoId={portcoId}
            reportDate={reportDate || undefined}
            onClose={() => setSendEmailOpen(false)}
            onSent={handleSent}
          />
        ) : null
      }
    />
  );
}

// =============================================================================
// Exported PortcoDetail component — routes to demo or live mode
// =============================================================================

export function PortcoDetail() {
  if (isDemoMode()) {
    return <DemoPortcoDetailContent />;
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={powerbiRequest}
      errorComponent={ErrorComponent}
      loadingComponent={Loading}
    >
      <LivePortcoDetailContent />
    </MsalAuthenticationTemplate>
  );
}
